import { forEach, map, orderBy } from 'lodash';

import CoreUtils from '../common/utils/CoreUtils';
import DateTimeUtils from '../common/utils/DateTimeUtils';
import NumberUtils from '../common/utils/NumberUtils';
import StringUtils from '../common/utils/StringUtils';
import UserUtils from '../common/utils/UserUtils';

import EnergyValue from '../common/value-types/EnergyValue';
import EnergyValueMask from '../common/value-types/EnergyValueMask';
import { EnergyValueViewMode } from '../common/value-types/EnergyValueViewMode';
import MetadataHelper from '../common/value-types/MetadataHelper';

import CorePropertyHelper from './property-helpers/CorePropertyHelper';

import ScheduleFieldValueOverrideRenderer from './ScheduleFieldValueOverrideRenderer';

const cutoffCheckIntervalMs = 1000;
const cutoffCheckMarginMs = 5000;

const scheduleDateKeyField = 'scheduleDateKey';

export const hlhEntryField = 'hlhEntryField';
export const llhEntryField = 'llhEntryField';
export const atcEntryField = 'atcEntryField';

export const hlhCode = 'HLH';
export const llhCode = 'LLH';
export const atcCode = 'ATC';

export default class ScheduleDataModel {
  static scheduleIdProperty = 'scheduleId';
  static enableEditProperty = 'enableEditValues';

  static allElectableHourFields = '---AllElectableHours---';
  static sameValue = '---SameValue---';
  static aboveMinimumValue = '---AboveMinimumValue---';
  static belowMaximumValue = '---BelowMaximumValue---';
  static anyElectableHourFields = '---AnyElectableHours---';
  static anyValue = '---AnyValue---';
  static referenceValue = '---ReferenceValue---';
  static minimumValue = '---MinimumValue---';
  static maximumValue = '---MaximumValue---';

  constructor(locale, counterparty, commandDataModel) {
    this.locale = locale;
    this.counterparty = counterparty;
    this.commandDataModel = commandDataModel;

    this.serverUtcTimeOffsetMs = 0;
    this.showZeroValues = true;
    this.lastRetrievalTimeUtc = null;
    this.clientBackgroundUpdateIntervalMs = 0;

    this.isEditingEnabled = false;

    this.contracts = [];
    this.indexedContracts = {};
    this.dateIndexedSchedules = {};
    this.indexedPackageRules = {};

    this.allSchedules = [];
    this.viewSchedules = [];
    this.indexedSchedules = {};
    this.activeDataFields = [];
    this.scheduleEditHints = {};
    this.quickEntryEditHints = {};

    this.cutOffTimeout = null;
    this.backgroundUpdateTimeout = null;

    this.isModified = false;
    this.hasValidationErrors = false;

    this.currentFilter = null;
    this.currentDatacenterFilter = null;
  }

  shutdown() {
    this.clearCutoffTimeout();
    this.clearBackgroundUpdateTimeout();
  }

  setScheduleFilter(filter) {
    this.currentFilter = filter;
    this.triggerScheduleFiltering();
  }

  setDatacenterFilter(datacenterFilter) {
    this.currentDatacenterFilter = datacenterFilter;
    this.triggerScheduleFiltering();
  }

  processFullServerUpdate(scheduleUpdateDto) {
    this.contracts = [];
    this.indexedContracts = {};
    this.dateIndexedSchedules = {};
    this.indexedPackageRules = {};

    this.clearCutoffTimeout();
    this.clearBackgroundUpdateTimeout();

    this.serverUtcTimeOffsetMs = Date.parse(scheduleUpdateDto.serverUtcTime) - Date.now();
    this.currentMonth = scheduleUpdateDto.currentMonth;
    this.showZeroValues = scheduleUpdateDto.showZeroValues;
    this.lastRetrievalTimeUtc = scheduleUpdateDto.retrievalTimeUtc;
    this.clientBackgroundUpdateIntervalMs = scheduleUpdateDto.clientBackgroundUpdateIntervalMs;

    this.initializePackageRules(scheduleUpdateDto.packageRules);
    this.processContracts(scheduleUpdateDto);
    this.initializeEditFieldTriggers();
    this.initializeSchedules(scheduleUpdateDto.schedules);
    this.updateOverallStatus();
    this.processExternalData(scheduleUpdateDto.externalDatas);
    this.updateIsModified();

    this.triggerBackgroundUpdateTimeout(this.currentMonth);
    this.triggerCutoffTimeout();
  }

  processBackgroundServerUpdate(scheduleUpdateDto) {
    this.serverUtcTimeOffsetMs = Date.parse(scheduleUpdateDto.serverUtcTime) - Date.now();
    this.lastRetrievalTimeUtc = scheduleUpdateDto.retrievalTimeUtc;
    this.clientBackgroundUpdateIntervalMs = scheduleUpdateDto.clientBackgroundUpdateIntervalMs;

    this.processPartialServerUpdate(scheduleUpdateDto.schedules, true);
    this.processExternalData(scheduleUpdateDto.externalDatas);

    this.triggerBackgroundUpdateTimeout(this.currentMonth);
  }

  processServerSubmitResult(scheduleDtos) {
    this.processPartialServerUpdate(scheduleDtos, false);
  }

  processContracts(scheduleUpdateDto) { }
  processExternalData(externalDatas) { }
  getDefaultSortCriteria() { }
  updateScheduleUsage(schedule, updatedDataFields) { }
  updateOverallStatus() { }
  establishEditFieldTriggers(editFieldTriggers) { }
  postProcessUpdatedDataFields(schedule, updatedDataFields) { }

  onScheduleCreating(schedule, contract) { }
  onScheduleUpdated(schedule, isServerUpdate, isPeerUpdate, updatedDataFields, quickEntryHints) { }

  onSchedulePropertiesCreating(schedule, contract, scheduleDto) { }
  onSchedulePropertiesServerUpdate(schedule, contract, scheduleDto, isPeerUpdate, updatedDataFields) { }
  onSchedulePropertiesModifying(schedule, contract, scheduleUpdateSet, updatedDataFields) { }
  onSchedulePropertiesEditing(schedule, dataFieldHints, updatedDataFields) { }
  onSchedulePropertiesCutoffCheck(schedule, serverUtcTimeMs, warningPeriodMs, updatedDataFields) { }
  onSchedulePropertiesReverting(schedule, updatedDataFields) { }
  onSchedulePropertiesSerialize(schedule, scheduleSubmissionDto) { }

  onSchedulesInitialized(schedules) { }

  getCurrentModifications() {
    var scheduleSubmissionDtos = [];

    CoreUtils.iterateProperties(this.indexedSchedules, (scheduleId, schedule) => {

      if (!schedule.isScheduleModified || schedule.isComputedSchedule || !schedule.enableEditValues)
        return;

      var scheduleSubmissionDto = {
        scheduleDate: schedule.scheduleDate,
        contractCode: schedule.contractCode,
        metaValues: {},
        hourValues: [],
        respectCutoffTimes: false
      };

      forEach(schedule.hourlyDataFields, dataField => {
        var metadataField = MetadataHelper.resolveMetadataField(dataField);
        if (schedule[metadataField] === undefined)
          return;

        if (schedule[metadataField].modifiedMask.none)
          return;

        scheduleSubmissionDto.hourValues.push({
          dataField: dataField,
          energyValue: schedule[dataField].serialize()
        });
      });

      this.onSchedulePropertiesSerialize(schedule, scheduleSubmissionDto);

      if (scheduleSubmissionDto.hourValues.length > 0
        || CoreUtils.getPropertyCount(scheduleSubmissionDto.metaValues) > 0)
        scheduleSubmissionDtos.push(scheduleSubmissionDto);
    });

    return scheduleSubmissionDtos;
  }

  revertCurrentModifications() {
    var scheduleUpdates = [];

    CoreUtils.iterateProperties(this.indexedSchedules, (_, schedule) => {
      if (!schedule.isScheduleModified)
        return;

      var updatedDataFields = [];

      forEach(schedule.hourlyDataFields, dataField => {
        var metadataField = MetadataHelper.resolveMetadataField(dataField);
        if (schedule[metadataField] === undefined)
          return;

        if (schedule[metadataField].modifiedMask.none)
          return;

        schedule[dataField] = schedule[metadataField].originalEnergyValue;

        updatedDataFields.push(dataField);

        schedule[metadataField].modifiedMask = new EnergyValueMask(false);
        schedule[metadataField].overriddenMask = new EnergyValueMask(false);
        schedule[metadataField].overriddenTime = null;
        schedule[metadataField].overriddenByUser = null;
        schedule[metadataField].overrideContentRender = null;
      });

      this.onSchedulePropertiesReverting(schedule, updatedDataFields);

      if (updatedDataFields.length > 0) {
        this.onScheduleUpdated(schedule, false, false, updatedDataFields, {});
        this.updateScheduleUsage(schedule, updatedDataFields);
        this.postProcessUpdatedDataFields(schedule, updatedDataFields);

        scheduleUpdates.push({
          scheduleId: schedule.scheduleId,
          dataFields: updatedDataFields
        });

        schedule.isScheduleModified = false;
      }
    });

    if (scheduleUpdates.length === 0)
      return;

    this.updateOverallStatus();
    this.updateIsModified();

    if (CoreUtils.isDefinedAndNotNull(this.commandDataModel))
      this.commandDataModel.updateSchedules(scheduleUpdates);
  }

  resolveScheduleById(scheduleId) {
    var schedule = this.indexedSchedules[scheduleId];
    return CoreUtils.isDefinedAndNotNull(schedule)
      ? schedule
      : null;
  }

  getPreviousHourField(scheduleId, dataField) {
    var schedule = this.indexedSchedules[scheduleId];
    if (CoreUtils.isUndefinedOrNull(schedule))
      return null;

    var index = schedule.hourlyDataFields.indexOf(dataField) - 1;
    return index >= 0
      ? schedule.hourlyDataFields[index]
      : null;
  }

  getNextHourField(scheduleId, dataField) {
    var schedule = this.indexedSchedules[scheduleId];
    if (CoreUtils.isUndefinedOrNull(schedule))
      return null;

    var index = schedule.hourlyDataFields.indexOf(dataField) + 1;
    return index < schedule.hourlyDataFields.length
      ? schedule.hourlyDataFields[index]
      : null;
  }

  getHourFieldSubhourlyResolution(schedule) {
    if (CoreUtils.isUndefinedOrNull(schedule))
      return 0;

    var contract = this.resolveContract(schedule.contractCode);
    if (CoreUtils.isUndefinedOrNull(contract))
      return 0;

    return contract.subhourlyResolution;
  }

  isElectableHourField(schedule, dataField) {
    if (CoreUtils.isUndefinedOrNull(schedule))
      return false;

    if (CoreUtils.isUndefinedOrNull(schedule.hourlyDataFields))
      return false;

    if (schedule.hourlyDataFields.indexOf(dataField) === -1)
      return false;

    var metadataField = MetadataHelper.resolveMetadataField(dataField);
    if (schedule[metadataField] === undefined)
      return false;

    return schedule[metadataField].isElectableHour && schedule.enableEditValues;
  }

  onScheduleCreated(schedule) {
    this.registerSchedulePackage(schedule);

    schedule[hlhEntryField] = new EnergyValue(0);
    schedule[llhEntryField] = new EnergyValue(0);
    schedule[atcEntryField] = new EnergyValue(0);
  }

  onFilterViewSchedule(contract, schedule) {
    return true;
  }

  canModifyFieldValue(schedule, dataField) {
    if (dataField === hlhEntryField)
      return this.anyMatchingFieldsUpdatable(schedule, hlhCode);
    else if (dataField === llhEntryField)
      return this.anyMatchingFieldsUpdatable(schedule, llhCode);
    else if (dataField === atcEntryField)
      return this.anyMatchingFieldsUpdatable(schedule, null);

    if (!this.isElectableHourField(schedule, dataField))
      return false;

    if (!schedule.enableEditValues || schedule.isComputedSchedule)
      return false;

    var metadataField = MetadataHelper.resolveMetadataField(dataField);
    if (schedule[metadataField] === undefined)
      return false;

    return schedule[metadataField].electionCutoffs.some(
      electionCutoff => !electionCutoff.isPastElectionCutoff
    ) || schedule.enablePostCutoffChanges;
  }

  canEditFieldValue(schedule, dataField) {
    return this.canModifyFieldValue(schedule, dataField);
  }

  anyMatchingFieldsUpdatable(schedule, hourTypeCode) {
    if (!schedule.enableEditValues || schedule.isComputedSchedule)
      return false;

    return schedule.hourlyDataFields.some(dataField =>
      this.isHourQuickEntryModifiable(schedule, dataField, hourTypeCode)
    );
  }

  isHourQuickEntryModifiable(schedule, dataField, hourTypeCode) {
    var metadataField = MetadataHelper.resolveMetadataField(dataField);
    return (CoreUtils.isUndefinedOrNull(hourTypeCode) || schedule[metadataField].hourTypeCode === hourTypeCode)
      && schedule[metadataField].isElectableHour
      && schedule.enableEditValues
      && (schedule.enablePostCutoffChanges || schedule[metadataField].electionCutoffs.some(electionCutoff => !electionCutoff.isPastElectionCutoff));
  }

  onFieldValuesEditing(schedule, dataFieldValues) {
    if (CoreUtils.getPropertyCount(dataFieldValues) === 1) {
      var hlhValue = dataFieldValues[hlhEntryField];
      var llhValue = dataFieldValues[llhEntryField];
      var atcValue = dataFieldValues[atcEntryField];

      if (CoreUtils.isDefinedAndNotNull(hlhValue)) {
        delete dataFieldValues[hlhEntryField];
        this.retrieveUpdatesForMatchingFields(schedule, dataFieldValues, hlhCode, hlhValue);
        this.quickEntryEditHints[hlhEntryField] = hlhValue;
      }

      if (CoreUtils.isDefinedAndNotNull(llhValue)) {
        delete dataFieldValues[llhEntryField];
        this.retrieveUpdatesForMatchingFields(schedule, dataFieldValues, llhCode, llhValue);
        this.quickEntryEditHints[llhEntryField] = llhValue;
      }

      if (CoreUtils.isDefinedAndNotNull(atcValue)) {
        delete dataFieldValues[atcEntryField];
        this.retrieveUpdatesForMatchingFields(schedule, dataFieldValues, null, atcValue);
        this.quickEntryEditHints[atcEntryField] = atcValue;
      }
    }

    var dataFields = [];

    CoreUtils.iterateProperties(
      dataFieldValues,
      (dataField, _) => dataFields.push(dataField)
    );

    this.scheduleEditHints = {
      [schedule.scheduleId]: dataFields
    };

    return dataFields.every(
      dataField => this.isElectableHourField(schedule, dataField)
    );
  }

  onFieldValuesEdited(schedule) {
    if (CoreUtils.isUndefinedOrNull(schedule))
      return;

    var dataFieldHints = this.scheduleEditHints[schedule.scheduleId];
    if (CoreUtils.isUndefinedOrNull(dataFieldHints))
      dataFieldHints = [];

    var quickEntryHints = this.quickEntryEditHints;

    this.scheduleEditHints = {};
    this.quickEntryEditHints = {};

    var contract = this.resolveContract(schedule.contractCode);

    this.processScheduleEditDataFieldHints(schedule, contract, dataFieldHints);

    var isScheduleModified = schedule.isScheduleModified;
    schedule.isScheduleModified = false;

    var updatedDataFields = [];

    for (var index = 0; index < schedule.hourlyDataFields.length; index++) {
      var dataField = schedule.hourlyDataFields[index];
      var metadataField = MetadataHelper.resolveMetadataField(dataField);

      var modifiedMask = schedule[dataField].getEqualityMask(
        schedule[metadataField].originalEnergyValue
      ).inverse();

      if (schedule.enableEditValues && modifiedMask.any)
        schedule.isScheduleModified = true;

      if (!modifiedMask.equals(schedule[metadataField].modifiedMask)
        || dataFieldHints.indexOf(dataField) !== -1)
        updatedDataFields.push(dataField);

      schedule[metadataField].modifiedMask = modifiedMask;

      schedule[metadataField].overriddenMask = schedule[metadataField].overriddenMask.and(
        schedule[metadataField].modifiedMask.inverse(),
        contract.subhourlyResolution
      );

      if (schedule[metadataField].overriddenMask.none) {
        schedule[metadataField].overriddenTime = null;
        schedule[metadataField].overriddenByUser = null;
        schedule[metadataField].overrideContentRender = null;
      }
    }

    this.onSchedulePropertiesEditing(schedule, dataFieldHints, updatedDataFields);

    this.onScheduleUpdated(schedule, false, false, updatedDataFields, quickEntryHints);
    this.updateScheduleUsage(schedule, updatedDataFields);
    this.updateOverallStatus();
    this.postProcessUpdatedDataFields(schedule, updatedDataFields);

    if (CoreUtils.isDefinedAndNotNull(this.commandDataModel))
      this.commandDataModel.updateSchedules([{
        scheduleId: schedule.scheduleId,
        dataFields: updatedDataFields
      }]);

    if (schedule.isScheduleModified !== isScheduleModified)
      this.updateIsModified();
  }

  retrieveUpdatesForMatchingFields(schedule, dataFieldValues, hourTypeCode, energyValue) {
    if (!schedule.enableEditValues)
      return;

    schedule.hourlyDataFields.forEach(dataField => {
      if (this.isHourQuickEntryModifiable(schedule, dataField, hourTypeCode))
        dataFieldValues[dataField] = energyValue;
    });
  }

  showFieldValueElectionCutoffAlert(schedule, dataField) {
    if (!this.isElectableHourField(schedule, dataField))
      return false;

    var metadataField = MetadataHelper.resolveMetadataField(dataField);
    if (schedule[metadataField] === undefined)
      return false;

    return schedule[metadataField].electionCutoffs.some(
      electionCutoff => electionCutoff.showElectionCutoffAlert
    );
  }

  canElectFieldValue(schedule, dataField) {
    if (!this.isElectableHourField(schedule, dataField))
      return false;

    if (schedule.isComputedSchedule)
      return false;

    var metadataField = MetadataHelper.resolveMetadataField(dataField);
    if (schedule[metadataField] === undefined)
      return false;

    return schedule[metadataField].electionCutoffs.some(
      electionCutoff => !electionCutoff.isPastElectionCutoff
    );
  }

  areElectableHourFields(schedule, dataFieldValues) {
    var dataFields = [];

    CoreUtils.iterateProperties(
      dataFieldValues,
      (dataField, _) => dataFields.push(dataField)
    );

    return dataFields.every(
      dataField => this.isElectableHourField(schedule, dataField)
    );
  }

  getHourTypeHighlightCode(schedule, dataField) {
    if (!this.counterparty.highlightHlhLlhFields || schedule.isComputedSchedule)
      return '';

    if (dataField === hlhEntryField)
      return hlhCode;
    else if (dataField === llhEntryField)
      return llhCode;
    else if (dataField === atcEntryField)
      return atcCode;

    if (!this.isElectableHourField(schedule, dataField))
      return '';

    var metadataField = MetadataHelper.resolveMetadataField(dataField);
    if (schedule[metadataField] === undefined)
      return '';

    if (CoreUtils.isUndefinedOrNull(schedule[metadataField].hourTypeCode))
      return '';

    return schedule[metadataField].hourTypeCode;
  }

  filterUpdatingElectableHourFields(schedule, dataFieldValues, contract) {
    var hourlyEnergyMinimum = new EnergyValue(contract.hourlyEnergyMinimum);
    var hourlyEnergyMaximum = new EnergyValue(contract.hourlyEnergyMaximum);

    CoreUtils.iterateProperties(dataFieldValues, (dataField, value) => {
      if (schedule.hourlyDataFields.indexOf(dataField) === -1)
        return;

      if (!(value instanceof EnergyValue)
        || !value.isCompatibleResolution(schedule.subhourlyResolution))
        dataFieldValues[dataField] = schedule[dataField];

      value = value.applyMinimum(
        hourlyEnergyMinimum,
        contract.subhourlyResolution
      );

      value = value.applyMaximum(
        hourlyEnergyMaximum,
        contract.subhourlyResolution
      );

      var isLockedOutMask = this.resolveIsLockedOutMask(schedule, dataField);

      dataFieldValues[dataField] = schedule[dataField].overwrite(
        value,
        isLockedOutMask.inverse(),
        contract.subhourlyResolution
      );
    });

    return true;
  }

  resolveIsLockedOutMask(schedule, dataField) {
    return !schedule.enablePostCutoffChanges && !schedule.isComputedSchedule
      ? this.resolveIsPastElectionCutoffMask(schedule, dataField)
      : new EnergyValueMask(false);
  }

  resolveIsPastElectionCutoffMask(schedule, dataField) {
    var metadataField = MetadataHelper.resolveMetadataField(dataField);
    if (CoreUtils.isUndefinedOrNull(schedule[metadataField]))
      return new EnergyValueMask(false);

    return new EnergyValueMask(
      schedule[metadataField].electionCutoffs.map(
        electionCutoff => electionCutoff.isPastElectionCutoff
      )
    );
  }

  isComputedField(schedule, dataField) {
    return CoreUtils.isDefinedAndNotNull(schedule) && schedule.isComputedSchedule;
  }

  isFieldValueModified(schedule, dataField) {
    if (CoreUtils.isUndefinedOrNull(schedule))
      return false;

    if (schedule.isComputedSchedule)
      return false;

    var metadataField = MetadataHelper.resolveMetadataField(dataField);
    if (schedule[metadataField] === undefined)
      return false;

    return schedule[metadataField].modifiedMask.any;
  }

  isFieldValueFlagged(schedule, dataField) {
    if (CoreUtils.isUndefinedOrNull(schedule))
      return null;

    var flagContentField = MetadataHelper.resolveFlagContentField(dataField);
    return CoreUtils.isDefinedAndNotNull(schedule[flagContentField]);
  }

  isFieldValueFailingValidation(schedule, dataField) {
    if (CoreUtils.isUndefinedOrNull(schedule))
      return null;

    var metadataField = MetadataHelper.resolveMetadataField(dataField);
    if (schedule[metadataField] === undefined)
      return null;

    return CoreUtils.isDefinedAndNotNull(schedule[metadataField].validationContentRender);
  }

  isFieldValueOverridden(schedule, dataField) {
    if (CoreUtils.isUndefinedOrNull(schedule))
      return null;

    var metadataField = MetadataHelper.resolveMetadataField(dataField);
    if (schedule[metadataField] === undefined)
      return null;

    return CoreUtils.isDefinedAndNotNull(schedule[metadataField].overrideContentRender);
  }

  getFieldValueToolTipInfo(schedule, dataField) {
    if (CoreUtils.isUndefinedOrNull(schedule))
      return null;

    var metadataField = MetadataHelper.resolveMetadataField(dataField);
    if (CoreUtils.isDefinedAndNotNull(schedule[metadataField])) {
      if (CoreUtils.isDefinedAndNotNull(schedule[metadataField].validationContentRender))
        return {
          contentRender: schedule[metadataField].validationContentRender,
          position: {
            my: 'center bottom',
            at: 'center top'
          }
        };

      if (CoreUtils.isDefinedAndNotNull(schedule[metadataField].overrideContentRender))
        return {
          contentRender: schedule[metadataField].overrideContentRender,
          position: {
            my: 'center bottom',
            at: 'center top'
          }
        };
    }

    var flagContentField = MetadataHelper.resolveFlagContentField(dataField);
    if (CoreUtils.isDefinedAndNotNull(schedule[flagContentField]))
      return {
        contentRender: schedule[flagContentField],
        position: {
          my: 'left center',
          at: 'right center'
        }
      };

    return null;
  }

  getFirstAvailableSchedule() {
    var scheduleId = undefined;
    var stopSearch = false;

    for (var scheduleIndex = 0; scheduleIndex < this.viewSchedules.length; scheduleIndex++) {
      var schedule = this.viewSchedules[scheduleIndex];
      if (CoreUtils.isUndefinedOrNull(schedule))
        continue;

      if (!schedule.enableViewValues)
        continue;

      for (var dataFieldIndex = 0; dataFieldIndex < schedule.hourlyDataFields.length; dataFieldIndex++) {
        var dataField = schedule.hourlyDataFields[dataFieldIndex];

        var metadataField = MetadataHelper.resolveMetadataField(dataField);
        if (schedule[metadataField] === undefined)
          continue;

        if (schedule[metadataField].isElectableHour
          && schedule[metadataField].electionCutoffs.some(electionCutoff => !electionCutoff.isPastElectionCutoff)) {
          scheduleId = schedule.scheduleId;
          stopSearch = true;
          break;
        }
      }

      if (stopSearch)
        break;
    }

    if (scheduleId === undefined && this.viewSchedules.length > 0)
      scheduleId = this.viewSchedules[0].scheduleId;

    return scheduleId;
  }

  triggerCutoffTimeout() {
    this.cutOffTimeout = setTimeout(this.onCutoffTimer.bind(this), cutoffCheckIntervalMs);
  }

  clearCutoffTimeout() {
    if (this.cutOffTimeout === null)
      return;

    clearTimeout(this.cutOffTimeout);
    this.cutOffTimeout = null;
  }

  onCutoffTimer() {
    this.cutOffTimeout = null;

    var scheduleUpdates = [];
    CoreUtils.iterateProperties(this.indexedSchedules, (_, schedule) => {
      var updatedDataFields = [];
      this.checkScheduleCutoff(schedule, updatedDataFields, false, true);

      if (updatedDataFields.length > 0) {
        this.postProcessUpdatedDataFields(schedule, updatedDataFields);

        scheduleUpdates.push({
          scheduleId: schedule.scheduleId,
          dataFields: updatedDataFields
        });
      }
    });

    if (scheduleUpdates.length === 0) {
      this.triggerCutoffTimeout();
      return;
    }

    this.updateOverallStatus();
    this.updateIsModified();

    if (CoreUtils.isDefinedAndNotNull(this.commandDataModel))
      this.commandDataModel.updateSchedules(scheduleUpdates);

    this.triggerCutoffTimeout();
  }

  triggerBackgroundUpdateTimeout(updateMonth) {
    if (this.clientBackgroundUpdateIntervalMs <= 0)
      return;

    this.backgroundUpdateTimeout = setTimeout(
      this.onBackgroundUpdateTimeout.bind(this),
      this.clientBackgroundUpdateIntervalMs,
      updateMonth
    );
  }

  clearBackgroundUpdateTimeout() {
    if (this.backgroundUpdateTimeout === null)
      return;

    clearTimeout(this.backgroundUpdateTimeout);
    this.backgroundUpdateTimeout = null;
  }

  onBackgroundUpdateTimeout(updateMonth) {
    this.backgroundUpdateTimeout = null;

    if (updateMonth !== this.currentMonth)
      return;

    if (CoreUtils.isDefinedAndNotNull(this.commandDataModel))
      this.commandDataModel.backgroundRefresh(this.lastRetrievalTimeUtc);
  }

  static resolveMetadataField(dataField) {
    return dataField + '_metadata';
  }

  registerContract(contract) {
    contract.enableViewValues = false;
    contract.enableEditValues = false;
    contract.enableResubmitValues = false;
    contract.enableRecalculateValues = false;

    this.indexedContracts[contract.code] = contract;
    this.contracts.push(contract);
  }

  resolveContract(contractCode) {
    return StringUtils.isNonEmptyString(contractCode)
      ? this.indexedContracts[contractCode]
      : null;
  }

  initializeEditFieldTriggers() {
    var editFieldTriggers = [];
    this.establishEditFieldTriggers(editFieldTriggers);

    this.editTriggerRecords = {};
    editFieldTriggers.forEach(editTrigger => {
      var editTriggerRecord = this.editTriggerRecords[editTrigger.triggerDataField];

      if (editTriggerRecord === undefined) {
        editTriggerRecord = {
          id: crypto.randomUUID(),
          dataField: editTrigger.triggerDataField,
          valueTriggers: []
        };

        this.editTriggerRecords[editTrigger.triggerDataField] = editTriggerRecord;
      }

      var triggerValueRecord = editTriggerRecord.valueTriggers
        .find(trigger => trigger.triggerValue === editTrigger.triggerValue);

      if (triggerValueRecord === undefined) {
        triggerValueRecord = {
          triggerValue: editTrigger.triggerValue,
          updateValues: {}
        };

        editTriggerRecord.valueTriggers.push(triggerValueRecord);
      }

      triggerValueRecord.updateValues[editTrigger.updateDataField] = editTrigger.updateValue;
    });
  }

  processUpdatedValues(
    schedule,
    contract,
    getUpdatedDataFieldsFunc,
    resolveUpdatedValueFunc,
    isTargetValueAlreadyUpdatedFunc,
    setTargetValueFunc
  ) {
    var triggerIdsProcessed = [];
    var nextDataFields = [];

    var anyElectableFieldsTriggerRecord = this.editTriggerRecords[ScheduleDataModel.anyElectableHourFields];
    var hourlyEnergyMinimum = new EnergyValue(contract.hourlyEnergyMinimum);
    var hourlyEnergyMaximum = new EnergyValue(contract.hourlyEnergyMaximum);

    var currentDataFields = getUpdatedDataFieldsFunc();
    while (currentDataFields.length > 0) {
      currentDataFields.forEach(currentDataField => {
        var editTriggerRecord = undefined;
        if (schedule.electableDataFields.indexOf(currentDataField) !== -1 && anyElectableFieldsTriggerRecord !== undefined)
          editTriggerRecord = anyElectableFieldsTriggerRecord;

        if (editTriggerRecord === undefined)
          editTriggerRecord = this.editTriggerRecords[currentDataField];

        if (editTriggerRecord === undefined)
          return;

        if (triggerIdsProcessed.indexOf(editTriggerRecord.id) !== -1)
          return;

        triggerIdsProcessed.push(editTriggerRecord.id);

        var triggerValue = resolveUpdatedValueFunc(currentDataField);

        var isLessThanOrEqualFunc = triggerValue instanceof EnergyValue
          ? (value1, value2) => value1.validateMaximum(value2)
          : (value1, value2) => value1 <= value2;

        var isMoreThanOrEqualFunc = triggerValue instanceof EnergyValue
          ? (value1, value2) => value1.validateMinimum(value2)
          : (value1, value2) => value1 >= value2;

        var triggerValueRecords = editTriggerRecord.valueTriggers
          .filter(
            trigger => trigger.triggerValue === triggerValue
              || trigger.triggerValue === ScheduleDataModel.anyValue
              || (trigger.triggerValue === ScheduleDataModel.minimumValue && isLessThanOrEqualFunc(triggerValue, hourlyEnergyMinimum))
              || (trigger.triggerValue === ScheduleDataModel.aboveMinimumValue && !isLessThanOrEqualFunc(triggerValue, hourlyEnergyMinimum))
              || (trigger.triggerValue === ScheduleDataModel.maximumValue && isMoreThanOrEqualFunc(triggerValue, hourlyEnergyMaximum))
              || (trigger.triggerValue === ScheduleDataModel.belowMaximumValue && !isMoreThanOrEqualFunc(triggerValue, hourlyEnergyMaximum))
          );

        if (triggerValueRecords === undefined || triggerValueRecords.length === 0)
          return;

        triggerValueRecords.forEach(triggerValueRecord => {
          var targetDataFields = CoreUtils.getObjectPropertyNames(triggerValueRecord.updateValues);
          targetDataFields.forEach(targetDataField => {
            var dataFields = this.processTargetDataFields(schedule, targetDataField);
            dataFields.forEach(dataField => {

              if (isTargetValueAlreadyUpdatedFunc(dataField))
                return;

              var targetValue = triggerValueRecord.updateValues[targetDataField];
              if (typeof targetValue === 'function')
                targetValue = targetValue(schedule, contract);
              else if (targetValue === ScheduleDataModel.referenceValue) {
                var metadataField = MetadataHelper.resolveMetadataField(dataField);
                targetValue = schedule[metadataField].referenceValue;
              }
              else if (targetValue === ScheduleDataModel.minimumValue)
                targetValue = hourlyEnergyMinimum;
              else if (targetValue === ScheduleDataModel.maximumValue)
                targetValue = hourlyEnergyMaximum;
              else if (targetValue === ScheduleDataModel.sameValue)
                targetValue = triggerValue;

              setTargetValueFunc(dataField, targetValue);
              nextDataFields.push(dataField);
            });
          });
        });
      });

      currentDataFields = Array.from(nextDataFields);
      nextDataFields.length = 0;
    }
  }

  processTargetDataFields(schedule, targetDataField) {
    if (targetDataField !== ScheduleDataModel.allElectableHourFields)
      return [targetDataField];

    var dataFields = [];
    schedule.hourlyDataFields.forEach(hourlyDataField => {
      var metadataField = MetadataHelper.resolveMetadataField(hourlyDataField);
      if (schedule[metadataField].isElectableHour)
        dataFields.push(hourlyDataField);
    });

    return dataFields;
  }

  processScheduleUpdateSet(schedule, contract, scheduleUpdateSet) {
    this.processUpdatedValues(
      schedule,
      contract,
      () => CoreUtils.getObjectPropertyNames(scheduleUpdateSet.values),
      dataField => scheduleUpdateSet.values[dataField],
      targetDataField => scheduleUpdateSet.values[targetDataField] !== undefined,
      (targetDataField, targetDataValue) =>
        scheduleUpdateSet.values[targetDataField] = targetDataValue
    );
  }

  processScheduleEditDataFieldHints(schedule, contract, dataFieldHints) {
    this.processUpdatedValues(
      schedule,
      contract,
      () => dataFieldHints,
      dataField => schedule[dataField],
      targetDataField => dataFieldHints.indexOf(targetDataField) !== -1,
      (targetDataField, targetDataValue) => {
        this.setPropertyValue(contract, schedule, targetDataField, targetDataValue);
        dataFieldHints.push(targetDataField);
      }
    );
  }

  setPropertyValue(contract, schedule, dataField, value) {
    if (schedule.hourlyDataFields.indexOf(dataField) !== -1) {
      var metadataField = MetadataHelper.resolveMetadataField(dataField);
      this.updatePropertyValue(
        schedule,
        dataField,
        metadataField,
        contract.subhourlyResolution,
        value
      );
    }
    else
      schedule[dataField] = value;
  }

  updatePropertyValue(schedule, dataField, metadataField, subhourlyResolution, updateValue) {
    var previousValue = schedule[dataField];
    var previousModifiedMask = schedule[metadataField].modifiedMask;

    var isLockedOutMask = this.resolveIsLockedOutMask(schedule, dataField);

    schedule[dataField] = schedule[dataField].overwrite(
      updateValue,
      isLockedOutMask.inverse(),
      subhourlyResolution
    );

    schedule[metadataField].modifiedMask = schedule[dataField].getEqualityMask(
      schedule[metadataField].originalEnergyValue
    ).inverse();

    return !schedule[metadataField].modifiedMask.equals(previousModifiedMask)
      || !schedule[dataField].equals(previousValue);
  }

  triggerScheduleFiltering() {
    this.viewSchedules.length = 0;

    this.allSchedules.forEach(schedule => {
      if (this.onFilterViewSchedule(this.indexedContracts[schedule.contractCode], schedule))
        this.viewSchedules.push(schedule);
    });
  }

  initializeSchedules(scheduleDtos) {
    var schedules = this.convertToSchedules(scheduleDtos);
    
    var sortCriteria = this.getDefaultSortCriteria();
    var sortedSchedules = orderBy(schedules, sortCriteria.fields, sortCriteria.order);

    this.viewSchedules.length = 0;
    this.allSchedules = [];
    this.indexedSchedules = {};
    this.isEditingEnabled = false;

    forEach(sortedSchedules, schedule => {
      if (schedule.enableViewValues) {
        if (this.onFilterViewSchedule(this.indexedContracts[schedule.contractCode], schedule))
          this.viewSchedules.push(schedule);

        this.allSchedules.push(schedule);
      }

      this.indexedSchedules[schedule.scheduleId] = schedule;

      if (schedule.enableEditValues && !schedule.isComputedSchedule)
        this.isEditingEnabled = true;
    });

    this.activeDataFields = [];
    forEach(schedules, schedule => {
      forEach(schedule.hourlyDataFields, dataField => {
        if (!this.activeDataFields.includes(dataField))
          this.activeDataFields.push(dataField);
      });
    });

    this.onSchedulesInitialized(this.viewSchedules);

    if (this.isModified) {
      this.isModified = false;

      if (CoreUtils.isDefinedAndNotNull(this.commandDataModel))
        this.commandDataModel.updateProperties({
          isModified: this.isModified
        });
    }
  }

  convertToSchedules(scheduleDtos) {
    return map(scheduleDtos, this.convertToSchedule.bind(this));
  }

  convertToSchedule(scheduleDto) {
    var contract = this.resolveContract(scheduleDto.contractCode);

    var schedule = {
      scheduleId: scheduleDto.scheduleId,
      scheduleDate: scheduleDto.scheduleDate,
      loadDayType: scheduleDto.loadDayType,
      localDateMs: Date.parse(scheduleDto.scheduleDate),
      weekday: DateTimeUtils.formatShortWeekday(scheduleDto.scheduleDate, this.locale),
      tradingDate: scheduleDto.tradingDate,
      weekOf: scheduleDto.weekOf,
      contractCode: contract.code,
      contractDisplayName: contract.displayName,
      isComputedSchedule: scheduleDto.isComputedSchedule,
      enableViewValues: scheduleDto.enableViewValues,
      enableEditValues: scheduleDto.enableEditValues,
      enableResubmitValues: scheduleDto.enableResubmitValues,
      enableRecalculateValues: scheduleDto.enableRecalculateValues,
      enablePostCutoffChanges: scheduleDto.enablePostCutoffChanges,
      firstUtcElectionCutoffMs: DateTimeUtils.parseDtoDateTime(scheduleDto.firstUtcElectionCutoff),
      isScheduleModified: false,
      isCompletelyPastCutoff: false,
      isGrayedOut: false,
      energyValueViewMode: EnergyValueViewMode.energyValue,
      hourlyDataFields: [],
      electableDataFields: [],
      lastUpdatedTime: scheduleDto.lastUpdatedTime,
      lastUpdatedUser: scheduleDto.lastUpdatedUser,
      lastUpdatedUserShort: UserUtils.formatShortUsername(scheduleDto.lastUpdatedUser),
      locale: this.locale,
      subhourlyResolution: contract.subhourlyResolution,
      showZeroValues: this.showZeroValues
    };

    if (schedule.enableViewValues)
      contract.enableViewValues = true;
    if (schedule.enableEditValues)
      contract.enableEditValues = true;
    if (schedule.enableResubmitValues)
      contract.enableResubmitValues = true;
    if (schedule.enableRecalculateValues)
      contract.enableRecalculateValues = true;

    this.onScheduleCreating(schedule, contract);

    forEach(scheduleDto.hourValues, scheduleHourValueDto => {
      var dataField = scheduleHourValueDto.dataField;
      var metadataField = MetadataHelper.resolveMetadataField(dataField);

      schedule[metadataField] = {
        dataField: dataField,
        hourTypeCode: scheduleHourValueDto.hourTypeCode,
        isElectableHour: scheduleHourValueDto.isElectableHour,
        electionCutoffs: scheduleHourValueDto.utcElectionCutoffs.map(
          utcElectionCutoff => CorePropertyHelper.createElectionCutoff(utcElectionCutoff)
        ),
        modifiedMask: new EnergyValueMask(false),
        overriddenMask: new EnergyValueMask(false),
        overriddenTime: null,
        overriddenByUser: null,
        overrideContentRender: null,
        validationContentRender: null
      };

      schedule[dataField] = new EnergyValue(scheduleHourValueDto.energyValue);
      schedule[metadataField].referenceValue = new EnergyValue(scheduleHourValueDto.referenceValue);
      schedule[metadataField].originalEnergyValue = schedule[dataField];

      schedule.hourlyDataFields.push(dataField);

      if (scheduleHourValueDto.isElectableHour)
        schedule.electableDataFields.push(dataField);
    });

    this.onSchedulePropertiesCreating(schedule, contract, scheduleDto);
    this.onScheduleCreated(schedule);
    this.updateScheduleUsage(schedule, []);
    this.checkScheduleCutoff(schedule, [], true, false);

    return schedule;
  }

  getEditorContext(scheduleId, dataField) {
    var schedule = this.indexedSchedules[scheduleId];
    if (CoreUtils.isUndefinedOrNull(schedule) || schedule.hourlyDataFields.indexOf(dataField) === -1)
      return null;

    var contract = this.resolveContract(schedule.contractCode);

    var metadataField = MetadataHelper.resolveMetadataField(dataField);
    if (CoreUtils.isUndefinedOrNull(schedule[metadataField]))
      return null;

    var electionCutoffMask = new EnergyValueMask(
      schedule[metadataField].electionCutoffs.map(
        electionCutoff => electionCutoff.isPastElectionCutoff
      )
    );

    var editLockoutMask = new EnergyValueMask(
      !this.canEditFieldValue(schedule, dataField)
    );

    return {
      locale: this.locale,
      contractDisplayName: contract.displayName,
      subhourlyResolution: contract.subhourlyResolution,
      scheduleDate: schedule.scheduleDate,
      energyValue: schedule[dataField],
      modifiedMask: schedule[metadataField].modifiedMask,
      overriddenMask: schedule[metadataField].overriddenMask,
      lockoutMask: !schedule.enablePostCutoffChanges
        ? electionCutoffMask.or(editLockoutMask, contract.subhourlyResolution)
        : new EnergyValueMask(false),
      electionCutOffMask: electionCutoffMask,
      electionCutoffAlertMask: new EnergyValueMask(
        schedule[metadataField].electionCutoffs.map(
          electionCutoff => electionCutoff.showElectionCutoffAlert
        )
      )
    };
  }

  submitEditorUpdate(scheduleId, dataField, energyValue) {
    if (!(energyValue instanceof EnergyValue))
      return;

    var schedule = this.indexedSchedules[scheduleId];
    if (CoreUtils.isUndefinedOrNull(schedule) || schedule.hourlyDataFields.indexOf(dataField) === -1)
      return;

    if (!schedule.enableEditValues || schedule.isComputedSchedule)
      return;

    var dataFieldValues = {
      [dataField]: energyValue
    };

    if (!this.onFieldValuesEditing(schedule, dataFieldValues))
      return;

    var scheduleUpdateSet = {
      scheduleId: scheduleId,
      values: {
        [dataField]: dataFieldValues[dataField]
      }
    };

    this.updateSchedules([scheduleUpdateSet], true);
  }

  processPartialServerUpdate(scheduleDtos, isPeerUpdate) {
    var scheduleUpdates = [];

    forEach(scheduleDtos, scheduleDto => {
      var schedule = this.indexedSchedules[scheduleDto.scheduleId];
      var contract = this.resolveContract(scheduleDto.contractCode);

      if (CoreUtils.isUndefinedOrNull(schedule))
        return;

      if (scheduleDto.lastUpdatedTime === schedule.lastUpdatedTime)
        return;

      var updatedDataFields = [];
      schedule.isScheduleModified = false;

      forEach(scheduleDto.hourValues, scheduleHourValueDto => {
        var metadataField = MetadataHelper.resolveMetadataField(scheduleHourValueDto.dataField);

        var serverUpdateMask = this.processValuesServerUpdate(
          scheduleHourValueDto,
          schedule,
          metadataField,
          contract.subhourlyResolution
        );

        if (serverUpdateMask.any) {
          if (isPeerUpdate) {
            schedule[metadataField].overriddenTime = scheduleDto.lastUpdatedTime;
            schedule[metadataField].overriddenByUser = scheduleDto.lastUpdatedUser;
            schedule[metadataField].overrideContentRender = () => ScheduleFieldValueOverrideRenderer(
              schedule[metadataField].overriddenByUser,
              schedule[metadataField].overriddenTime,
              this.locale
            );
            schedule[metadataField].overriddenMask = schedule[metadataField].overriddenMask
              .or(serverUpdateMask, contract.subhourlyResolution);
          } else {
            schedule[metadataField].modifiedMask = new EnergyValueMask(false);

            schedule[metadataField].overriddenTime = null;
            schedule[metadataField].overriddenByUser = null;
            schedule[metadataField].overrideContentRender = null;
            schedule[metadataField].overriddenMask = new EnergyValueMask(false);
          }

          updatedDataFields.push(scheduleHourValueDto.dataField);
        }

        if (schedule.enableEditValues && schedule[metadataField].modifiedMask.any)
          schedule.isScheduleModified = true;
      });

      if (schedule.lastUpdatedTime !== scheduleDto.lastUpdatedTime) {
        schedule.lastUpdatedTime = scheduleDto.lastUpdatedTime;
        updatedDataFields.push('lastUpdatedTime');
      }

      if (schedule.lastUpdatedUser !== scheduleDto.lastUpdatedUser) {
        schedule.lastUpdatedUser = scheduleDto.lastUpdatedUser;
        updatedDataFields.push('lastUpdatedUser');

        schedule.lastUpdatedUserShort = UserUtils.formatShortUsername(scheduleDto.lastUpdatedUser);
        updatedDataFields.push('lastUpdatedUserShort');
      }

      this.onSchedulePropertiesServerUpdate(schedule, contract, scheduleDto, isPeerUpdate, updatedDataFields);

      if (updatedDataFields.length > 0) {
        this.onScheduleUpdated(schedule, true, isPeerUpdate, updatedDataFields, {});
        this.updateScheduleUsage(schedule, updatedDataFields);
        this.postProcessUpdatedDataFields(schedule, updatedDataFields);

        scheduleUpdates.push({
          scheduleId: schedule.scheduleId,
          dataFields: updatedDataFields
        });
      }
    });

    if (scheduleUpdates.length === 0)
      return;

    this.updateOverallStatus();
    this.updateIsModified();

    if (CoreUtils.isDefinedAndNotNull(this.commandDataModel))
      this.commandDataModel.updateSchedules(scheduleUpdates);
  }

  processValuesServerUpdate(scheduleHourValueDto, schedule, metadataField, subhourlyResolution) {
    var serverEnergyValue = new EnergyValue(scheduleHourValueDto.energyValue);
    var serverReferenceValue = new EnergyValue(scheduleHourValueDto.referenceValue);

    if (schedule[metadataField].originalEnergyValue.equals(serverEnergyValue)
      && schedule[metadataField].referenceValue.equals(serverReferenceValue))
      return new EnergyValueMask(false);

    schedule[scheduleHourValueDto.dataField] = serverEnergyValue.overwrite(
      schedule[scheduleHourValueDto.dataField],
      schedule[metadataField].modifiedMask,
      subhourlyResolution
    );

    var serverUpdateReferenceMask = schedule[metadataField].referenceValue
      .getEqualityMask(serverReferenceValue).inverse();

    var serverUpdateEnergyMask = schedule[metadataField].originalEnergyValue
      .getEqualityMask(serverEnergyValue).inverse();

    schedule[metadataField].referenceValue = serverReferenceValue;
    schedule[metadataField].originalEnergyValue = serverEnergyValue;

    return serverUpdateEnergyMask.or(serverUpdateReferenceMask, schedule.subhourlyResolution);
  }

  static generateDateKey(isoDate) {
    var date = new Date(isoDate);
    return 'd' + NumberUtils.padZerosIfNeeded(date.getFullYear(), 4)
      + NumberUtils.padZerosIfNeeded(date.getMonth() + 1, 2)
      + NumberUtils.padZerosIfNeeded(date.getDate(), 2);
  }

  static generateScheduleDateKey(dateKey, contractCode) {
    return dateKey + '_' + contractCode;
  }

  registerSchedulePackage(schedule) {
    var dateKey = ScheduleDataModel.generateDateKey(schedule.scheduleDate);
    var scheduleDateKey = ScheduleDataModel.generateScheduleDateKey(
      dateKey,
      schedule.contractCode
    );

    schedule[scheduleDateKeyField] = scheduleDateKey;
    this.dateIndexedSchedules[scheduleDateKey] = schedule;
  }

  initializePackageRules(packageRulesDto) {
    CoreUtils.iterateProperties(packageRulesDto, (contractCode, datePackageRuleDtos) => {
      this.indexedPackageRules[contractCode] = {};

      forEach(datePackageRuleDtos, datePackageRuleDto => {
        var dateKey = ScheduleDataModel.generateDateKey(datePackageRuleDto.date);

        this.indexedPackageRules[contractCode][dateKey] = {
          canConfigureIndependently: datePackageRuleDto.canConfigureIndependently,
          packageDateKeys: datePackageRuleDto.packageDates.map(date => ScheduleDataModel.generateDateKey(date))
        };
      });
    });
  }

  determinePackageRuleFlags(schedule, dataField, packageRuleRender) {
    var dateKey = ScheduleDataModel.generateDateKey(schedule.scheduleDate);

    var packageRule = this.indexedPackageRules[schedule.contractCode][dateKey];
    if (CoreUtils.isUndefinedOrNull(packageRule))
      return;

    var otherDates = packageRule.packageDateKeys
      .filter(packageDateKey => packageDateKey !== dateKey)
      .map(packageDateKey => {
        var packageScheduleDateKey = ScheduleDataModel.generateScheduleDateKey(packageDateKey, schedule.contractCode);
        return this.dateIndexedSchedules[packageScheduleDateKey];
      })
      .filter(packageSchedule => CoreUtils.isDefinedAndNotNull(packageSchedule))
      .map(packageSchedule => new Date(packageSchedule.scheduleDate));

    var flagContentField = MetadataHelper.resolveFlagContentField(dataField);
    schedule[flagContentField] = () => packageRuleRender(packageRule, otherDates);
  }

  getRelatedScheduleId(scheduleId, originalContractCode, relatedContractCode) {
    if (!StringUtils.isNonEmptyString(scheduleId) || !StringUtils.isNonEmptyString(originalContractCode)
      || !StringUtils.isNonEmptyString(relatedContractCode))
      return null;

    return scheduleId.replace(
      originalContractCode,
      relatedContractCode
    );
  }

  getRelatedSchedule(schedule, relatedContract) {
    if (CoreUtils.isUndefinedOrNull(schedule) || CoreUtils.isUndefinedOrNull(relatedContract))
      return null;

    var relatedScheduleId = this.getRelatedScheduleId(
      schedule.scheduleId,
      schedule.contractCode,
      relatedContract.code
    );

    var relatedSchedule = this.indexedSchedules[relatedScheduleId]
    return CoreUtils.isDefinedAndNotNull(relatedSchedule)
      ? relatedSchedule
      : null;
  }

  generateSingleValueScheduleUpdateSet(scheduleId, dataFields, value) {
    var values = {};
    dataFields.forEach(dataField => values[dataField] = value);

    return {
      scheduleId: scheduleId,
      values: values
    };
  }

  updateSchedules(scheduleUpdateSets, notifyNonChanges, bypassTriggers) {
    var scheduleUpdates = [];

    forEach(scheduleUpdateSets, scheduleUpdateSet => {
      var schedule = this.indexedSchedules[scheduleUpdateSet.scheduleId];
      var contract = this.resolveContract(schedule.contractCode);

      if (CoreUtils.isUndefinedOrNull(schedule))
        return;

      if (CoreUtils.isUndefinedOrNull(bypassTriggers) || !bypassTriggers)
        this.processScheduleUpdateSet(schedule, contract, scheduleUpdateSet);

      var updatedDataFields = [];
      schedule.isScheduleModified = false;

      forEach(schedule.hourlyDataFields, dataField => {
        var metadataField = MetadataHelper.resolveMetadataField(dataField);
        if (schedule[metadataField] === undefined)
          return;

        var updateValue = scheduleUpdateSet.values[dataField];

        if (updateValue === undefined || (!this.canModifyFieldValue(schedule, dataField) && !schedule.isComputedSchedule)) {
          if (schedule.enableEditValues && schedule[metadataField].modifiedMask.any)
            schedule.isScheduleModified = true;
          return;
        }

        var isHourValueUpdated = this.updatePropertyValue(
          schedule,
          dataField,
          metadataField,
          contract.subhourlyResolution,
          updateValue
        );

        if (isHourValueUpdated || notifyNonChanges)
          updatedDataFields.push(dataField);

        schedule[metadataField].overriddenMask = schedule[metadataField].overriddenMask.and(
          schedule[metadataField].modifiedMask.inverse(),
          contract.subhourlyResolution
        );

        if (schedule[metadataField].overriddenMask.none) {
          schedule[metadataField].overriddenTime = null;
          schedule[metadataField].overriddenByUser = null;
          schedule[metadataField].overrideContentRender = null;
        }

        if ((schedule.enableEditValues || schedule.isComputedSchedule) && schedule[metadataField].modifiedMask.any)
          schedule.isScheduleModified = true;
      });

      this.onSchedulePropertiesModifying(schedule, contract, scheduleUpdateSet, updatedDataFields);

      if (updatedDataFields.length > 0) {
        this.onScheduleUpdated(schedule, false, false, updatedDataFields, {});
        this.updateScheduleUsage(schedule, updatedDataFields);
        this.postProcessUpdatedDataFields(schedule, updatedDataFields);

        scheduleUpdates.push({
          scheduleId: schedule.scheduleId,
          dataFields: updatedDataFields
        });
      }
    });

    if (scheduleUpdates.length === 0)
      return 0;

    this.updateOverallStatus();
    this.updateIsModified();

    if (CoreUtils.isDefinedAndNotNull(this.commandDataModel))
      this.commandDataModel.updateSchedules(scheduleUpdates);

    return scheduleUpdates.length;
  }

  updateIsModified() {
    var isModified = false;

    CoreUtils.iterateProperties(this.indexedSchedules, (_, schedule) => {
      if (schedule.isScheduleModified)
        isModified = true;
    });

    if (isModified !== this.isModified) {
      this.isModified = isModified;

      if (CoreUtils.isDefinedAndNotNull(this.commandDataModel))
        this.commandDataModel.updateProperties({
          isModified: this.isModified
        });
    }
  }

  checkScheduleCutoff(schedule, updatedDataFields, isInitializing, validateEditorValues) {
    if (schedule.isCompletelyPastCutoff)
      return;

    var contract = this.resolveContract(schedule.contractCode);

    var serverUtcTimeMs = Date.now() + this.serverUtcTimeOffsetMs;
    var warningPeriodMs = CoreUtils.isDefinedAndNotNull(contract)
      ? contract.cutOffAdvanceWarningPeriodMs
      : 0;

    if (schedule.firstUtcElectionCutoffMs > serverUtcTimeMs + warningPeriodMs + cutoffCheckMarginMs)
      return;

    schedule.isCompletelyPastCutoff = true;
    schedule.isScheduleModified = false;

    schedule.hourlyDataFields.forEach(dataField => {
      var metadataField = MetadataHelper.resolveMetadataField(dataField);
      if (schedule[metadataField] === undefined)
        return;

      if (!schedule[metadataField].isElectableHour) {
        if (schedule.enableEditValues && schedule[metadataField].modifiedMask.any)
          schedule.isScheduleModified = true;

        return;
      }

      var cutoffStatusFlags = {
        cutOffStatusChanged: false,
        revertModifiedValues: false
      };

      schedule[metadataField].electionCutoffs.forEach(
        electionCutoff => CorePropertyHelper.processElectionCutoff(electionCutoff, serverUtcTimeMs, warningPeriodMs, cutoffStatusFlags)
      );

      if (cutoffStatusFlags.revertModifiedValues && schedule[metadataField].modifiedMask.any && validateEditorValues) {
        var electionCutoffMask = new EnergyValueMask(
          schedule[metadataField].electionCutoffs.map(
            electionCutoff => electionCutoff.isPastElectionCutoff
          )
        );

        schedule[dataField] = schedule[metadataField].originalEnergyValue.overwrite(
          schedule[dataField],
          electionCutoffMask.inverse(),
          contract.subhourlyResolution
        );

        schedule[metadataField].modifiedMask = schedule[dataField].getEqualityMask(
          schedule[metadataField].originalEnergyValue
        ).inverse();
      }

      if (schedule.enableEditValues && schedule[metadataField].modifiedMask.any)
        schedule.isScheduleModified = true;

      if (schedule[metadataField].electionCutoffs.some(electionCutoff => !electionCutoff.isPastElectionCutoff))
        schedule.isCompletelyPastCutoff = false;

      if (cutoffStatusFlags.cutOffStatusChanged)
        updatedDataFields.push(dataField);
    });

    this.onSchedulePropertiesCutoffCheck(schedule, serverUtcTimeMs, warningPeriodMs, updatedDataFields);

    if (!isInitializing && updatedDataFields.length > 0) {
      this.onScheduleUpdated(schedule, false, false, updatedDataFields, {});
      this.updateScheduleUsage(schedule, updatedDataFields);
    }
  }
}